import { createContext, useContext, useEffect, useState } from "react";
import { BrowserRouter, NavLink, Navigate, Route, Routes } from "react-router-dom";
import { AlertTriangle, Building2, CircleHelp, Clock, FileSignature, LogOut, Menu, PenLine, ShieldCheck } from "lucide-react";
import { api, publicApi } from "./lib/api";
import { completeSignIn, config, idToken, loadConfig, signIn, signOut } from "./lib/auth";
import type { Me } from "./lib/types";
import { Button, cx, ErrorNote, Spinner, ToastHost } from "./components/ui";
import { inputCls } from "./components/bits";
import { Lockup, MicrosoftMark, Wordmark } from "./components/icons";
import Dashboard from "./pages/Dashboard";
import EnvelopePage from "./pages/EnvelopePage";
import NewFromLink from "./pages/NewFromLink";
import { ACCOUNT_ERROR, SelfSign, SignerApp } from "./pages/Sign";
import Help from "./pages/Help";
import MySignature from "./pages/MySignature";
import CompanyAdmin from "./pages/CompanyAdmin";
import Platform from "./pages/Platform";
import { Join, Signup } from "./pages/Signup";
import SsoTest from "./pages/SsoTest";
import ConnectorCallback from "./pages/ConnectorCallback";
import Onboarding from "./pages/Onboarding";

// Runs once even under StrictMode's double effect: the sign-in code can only be exchanged once.
let boot: Promise<{ err: string | null; signedIn: boolean }> | null = null;
const MeContext = createContext<{ me: Me; reload: () => Promise<void> } | null>(null);
export const useMe = () => useContext(MeContext)!.me;
export const useReloadMe = () => useContext(MeContext)!.reload;

export default function App() {
  // Signers have no account: their pages never touch Cognito.
  const signer = /^\/s\/([A-Za-z0-9_-]{43})\/?$/.exec(location.pathname);
  if (signer) return <SignerApp token={signer[1]} />;
  const path = location.pathname.replace(/\/$/, "") || "/";
  // Public pages: help (outside signers read it), sign-up, invitations, the SSO test pop-up.
  if (path === "/help") return <Help />;
  if (path === "/signup") return <Signup />;
  if (path === "/join") return <Join />;
  if (path === "/sso-test") return <SsoTest />;
  return <SenderApp />;
}

function SenderApp() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "signin" | "ready">("loading");
  const loadMe = async () => setMe(await api<Me>("/me"));

  useEffect(() => {
    boot ??= (async () => {
      await loadConfig();
      const err = await completeSignIn();
      // A sign-in started on a signing link: go back to the signer app with the tokens (or the error).
      if (location.pathname.startsWith("/s/")) {
        if (err) sessionStorage.setItem(ACCOUNT_ERROR, err);
        location.reload();
        return new Promise<never>(() => {});
      }
      return { err, signedIn: Boolean(await idToken()) };
    })();
    boot
      .then(async ({ err, signedIn }) => {
        if (err) setError(err);
        if (!signedIn) return setState("signin");
        try {
          await loadMe();
          setState("ready");
        } catch (e) {
          setError((e as Error).message);
          setState("signin");
        }
      })
      .catch((e) => {
        setError((e as Error).message);
        setState("signin");
      });
  }, []);

  if (state === "loading") return <Spinner />;
  if (state === "signin" || !me) return <SignIn error={error} />;
  // Signed in, but no company yet: register one (platform admins can go straight to the console).
  if (!me.company && !me.isPlatformAdmin) return <Onboarding me={me} onDone={loadMe} />;
  return (
    <MeContext.Provider value={{ me, reload: loadMe }}>
      <BrowserRouter>
        <Routes>
          <Route path="/envelopes/:id/sign" element={<SelfSign />} />
          <Route
            path="*"
            element={
              <Shell>
                <Routes>
                  {me.company ? (
                    <>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/new" element={<NewFromLink />} />
                      <Route path="/signature" element={<MySignature />} />
                      <Route path="/envelopes/:id" element={<EnvelopePage />} />
                      {me.isAdmin && <Route path="/company/*" element={<CompanyAdmin />} />}
                      {me.isAdmin && <Route path="/company/envelopes/:id" element={<EnvelopePage admin />} />}
                      {me.isAdmin && <Route path="/connectors/callback" element={<ConnectorCallback />} />}
                    </>
                  ) : (
                    <Route path="/" element={<Navigate to="/platform" replace />} />
                  )}
                  {me.isPlatformAdmin && <Route path="/platform" element={<Platform />} />}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Shell>
            }
          />
        </Routes>
        <ToastHost />
      </BrowserRouter>
    </MeContext.Provider>
  );
}

/** Email first (design §4): a verified domain with SSO goes to the company IdP, everyone else to email + password. */
function SignIn({ error }: { error: string | null }) {
  const back = new URLSearchParams(location.search).get("back") || "/";
  const [email, setEmail] = useState(() => new URLSearchParams(location.search).get("email") ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(error);
  const [sso, setSso] = useState<{ provider: string; company: string } | null>(null);
  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      const d = await publicApi<{ method: string; provider?: string; company?: string }>("/discover", { email });
      if (d.method === "sso" && d.provider) {
        setSso({ provider: d.provider, company: d.company ?? "your company" });
        await signIn({ provider: d.provider, loginHint: email, back });
      } else await signIn({ loginHint: email, back });
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <AuthFrame>
      <h1 className="text-2xl font-bold font-display">Sign in</h1>
      <p className="text-sm text-muted mt-1">Send documents for e-signature</p>
      {err && (
        <div className="mt-5 text-left">
          <ErrorNote>{err}</ErrorNote>
        </div>
      )}
      <form
        className="mt-6 text-left space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void go();
        }}
      >
        <label className="block text-sm font-medium text-ink-2" htmlFor="email">
          Work email
        </label>
        <input id="email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={cx(inputCls, "h-11")} placeholder="you@company.com" />
        <Button variant="primary" type="submit" className="w-full h-11" busy={busy}>
          {sso ? (
            <>
              <MicrosoftMark className="size-4" /> Continuing to {sso.company}…
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </form>
      <p className="text-xs text-muted mt-4">If your company uses single sign-on, we'll take you to it.</p>
      <div className="mt-6 pt-5 border-t border-line text-sm">
        New to eSignIt?{" "}
        <a href="/signup" className="font-medium text-accent-text hover:underline">
          Register your company
        </a>
      </div>
      <p className="text-xs text-muted mt-4">Signing a document someone sent you? Use the link in your email; you don't need an account.</p>
    </AuthFrame>
  );
}

/** The centred card used by sign-in, sign-up and onboarding. */
export function AuthFrame({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <main className="min-h-full flex flex-col items-center justify-center p-4 gap-6">
      <a href={config()?.landingUrl ?? "/"} aria-label="eSignIt home">
        <Wordmark height={26} />
      </a>
      <div className={cx("w-full bg-surface border border-line rounded-2xl shadow-sm text-center overflow-hidden", wide ? "max-w-lg" : "max-w-sm")}>
        <div className="brand-bar h-1.5" aria-hidden />
        <div className="p-8">{children}</div>
      </div>
      <p className="text-xs text-muted">
        <a href="/help" className="hover:text-ink underline-offset-2 hover:underline">
          Help
        </a>{" "}
        ·{" "}
        <a href={`${config()?.landingUrl ?? "https://esignit.app"}/terms`} className="hover:text-ink underline-offset-2 hover:underline">
          Terms
        </a>{" "}
        ·{" "}
        <a href={`${config()?.landingUrl ?? "https://esignit.app"}/privacy`} className="hover:text-ink underline-offset-2 hover:underline">
          Privacy
        </a>{" "}
        · © {new Date().getFullYear()} eSignIt
      </p>
    </main>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const link = ({ isActive }: { isActive: boolean }) => cx("flex items-center gap-3 rounded-lg px-3 h-10 text-sm font-medium", isActive ? "bg-accent-chip text-accent-text" : "text-ink-2 hover:bg-surface-2");
  const nav = (
    <nav className="flex flex-col gap-1" onClick={() => setOpen(false)}>
      {me.company && (
        <>
          <NavLink to="/" end className={link}>
            <FileSignature className="size-4" /> Envelopes
          </NavLink>
          <NavLink to="/signature" className={link}>
            <PenLine className="size-4" /> My signature
          </NavLink>
          {me.isAdmin && (
            <NavLink to="/company" className={link}>
              <Building2 className="size-4" /> Company
            </NavLink>
          )}
        </>
      )}
      {me.isPlatformAdmin && (
        <NavLink to="/platform" className={link}>
          <ShieldCheck className="size-4" /> Platform
        </NavLink>
      )}
      <a href="/help" className={link({ isActive: false })}>
        <CircleHelp className="size-4" /> Help
      </a>
    </nav>
  );
  const account = (
    <div className="px-3 flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{me.name}</div>
        <div className="text-xs text-muted truncate">{me.company ? me.company.name : me.email}</div>
      </div>
      <button onClick={() => signOut()} className="p-2 rounded-lg text-muted hover:bg-surface-2" aria-label="Sign out" title="Sign out">
        <LogOut className="size-4" />
      </button>
    </div>
  );
  const status = me.company?.status;
  return (
    <div className="flex h-full">
      <aside className="hidden md:flex w-60 shrink-0 flex-col gap-6 border-r border-line bg-surface p-4">
        <Brand />
        {nav}
        <div className="mt-auto">{account}</div>
      </aside>
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-scrim" onClick={() => setOpen(false)}>
          <aside className="w-72 h-full bg-surface p-4 flex flex-col gap-6" onClick={(e) => e.stopPropagation()}>
            <Brand />
            {nav}
            <div className="mt-auto">{account}</div>
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden flex items-center gap-2 border-b border-line bg-surface px-3 h-14">
          <button onClick={() => setOpen(true)} className="p-2 rounded-lg hover:bg-surface-2" aria-label="Menu">
            <Menu className="size-5" />
          </button>
          <Brand />
        </header>
        {status === "pending" && (
          <div className="flex items-start gap-2 bg-warn-soft text-warn text-sm px-4 py-2.5 border-b border-line">
            <Clock className="size-4 mt-0.5 shrink-0" aria-hidden />
            <span>
              <strong>{me.company!.name} is waiting for approval.</strong> You can invite people, set things up and prepare drafts; sending starts once eSignIt approves your company (usually within one business day).
            </span>
          </div>
        )}
        {status === "suspended" && (
          <div className="flex items-start gap-2 bg-bad-soft text-bad text-sm px-4 py-2.5 border-b border-line">
            <AlertTriangle className="size-4 mt-0.5 shrink-0" aria-hidden /> <span>Sending is paused for {me.company!.name}. Contact eSignIt support.</span>
          </div>
        )}
        <main className="flex-1 min-h-0 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

export function Brand() {
  return (
    <div className="flex items-center px-1">
      <Lockup height={17} />
    </div>
  );
}
